import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import invariant from 'tiny-invariant';

import { IPageMetadata } from '../../interfaces/nextjs';

import { useDatadogRUM } from './hooks/useDatadogRUM';
import { useInitializeAmplitude } from './hooks/useInitializeAmplitude';
import { useSetUserProperties } from './hooks/useSetUserProperties';

type IProps = {
  children: ReactNode;
};

interface IState {
  blockDatadogRum: boolean;
  currentPage: string;
  currentPageTitle: string;
  currentPageId?: string;
  currentPageSubject?: string;
  previousPage: string | null;
  setCurrentPage: (page: IPageMetadata) => void;
  setBlockDatadogRum: (block: boolean) => void;
}

const AnalyticsContext = React.createContext<IState | null>({
  blockDatadogRum: false,
  currentPage: '',
  currentPageTitle: '',
  previousPage: null,
  setCurrentPage: () => {},
  setBlockDatadogRum: () => {},
});

function AnalyticsProvider({ children }: IProps) {
  const [blockDatadogRum, setBlockDatadogRum] = useState<boolean>(false);

  const [state, setState] = useState<{
    currentPage: IPageMetadata | null;
    previousPage: string | null;
  }>({
    currentPage: null,
    previousPage: null,
  });

  const setCurrentPage = useCallback((newPage: IPageMetadata) => {
    setState(prev => {
      if (prev.currentPage?._page !== newPage._page) {
        return {
          previousPage: prev.currentPage?._page ?? null,
          currentPage: newPage,
        };
      }
      return prev;
    });
  }, []);

  useDatadogRUM();
  useInitializeAmplitude();
  useSetUserProperties();

  const value = useMemo(
    () => ({
      blockDatadogRum: blockDatadogRum,
      currentPage: state.currentPage?._page ?? '',
      currentPageTitle: state.currentPage?._pageTitle ?? '',
      currentPageId: state.currentPage?._pageId,
      currentPageSubject: state.currentPage?._pageSubject,
      previousPage: state.previousPage,
      setCurrentPage,
      setBlockDatadogRum,
    }),
    [
      blockDatadogRum,
      state.currentPage?._page,
      state.currentPage?._pageTitle,
      state.currentPage?._pageId,
      state.currentPage?._pageSubject,
      state.previousPage,
      setCurrentPage,
    ],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

function useAnalytics(): IState {
  const context = React.useContext(AnalyticsContext);
  invariant(context !== null);

  return context;
}

export { AnalyticsProvider, useAnalytics };
